import { z } from "zod";
import React, { useEffect } from "react";
import { Script, type GetServerDataProps } from "gatsby";

import type { WithContext, AggregateRating } from "schema-dts";

import { parseGid } from "@shopify/hydrogen";

import { MetaComponent, type ImageProps } from "@bared/ui";

import {
  filterProjection,
  TemplateProductSchema,
  SettingSizeGuideSchema,
  SettingBiomechanicalFootbedSchema,
  SettingCustomisableFitSchema,
  SettingShippingAndReturnsSchema,
  SettingShoeCareInstructionsSchema,
} from "@bared/groq";

import type { ProductVariantFragment } from "types/storefront.generated";

import type { ProductCard } from "types/product";

import type { HeadProps, PageProps } from "~/types/gatsby";

import { ErrorPage } from "~/components/Error/Error";
import { Product } from "~/components/Product/Product";
import { GiftCard } from "~/components/Product/GiftCard";
import { ReviewsSentimentWidget } from "~/components/ReviewsSentimentWidget";

import { useSite } from "~/hooks/useSite";
import { useTrendii } from "~/hooks/useTrendii";
import { useKlaviyo } from "~/hooks/useKlaviyo";
import { useTripleWhale } from "~/hooks/useTripleWhale";
import { useNostoAnalytics } from "@hooks/useNostoAnalytics";
import { useGoogleTagManager } from "~/hooks/useGoogleTagManager";
import { useUpdateRecentlyViewed } from "~/hooks/useRecentlyViewed";

import { ReviewsProvider } from "~/providers/ReviewsProvider";

import { contextFromServerDataProps } from "~/lib/server";
import { seoConfigForPage, getSeoMeta, type SeoSchema } from "~/lib/seo";

import {
  PRODUCT_BY_HANDLE_QUERY,
  PRODUCT_CARD_BY_ID_QUERY,
  PRODUCT_RECOMMENDATIONS_QUERY,
} from "~/graphql/products";

import { Swatches } from "./Swatches";
import { AddToCartStack } from "./AddToCartStack";
import { AsSeenOnSection } from "./AsSeenOnSection";
import { ProductProvider } from "./ProductProvider";
import { HowToStyleSection } from "./HowToStyleSection";
import { ProductImageGallery } from "./ProductImageGallery";
import { ProductRecommendations } from "./ProductRecommendations";
import { FeatureAndShoeCareSheet } from "./FeatureAndShoeCareSheet";
import { ReviewNugget } from "./ReviewNugget";
import { SeoFeatureDescription } from "./SeoFeatureDescription";
import { ProductUpsells } from "./ProductUpsells";

const productTemplateQuery = filterProjection(
  TemplateProductSchema,
  `_type == "templateProduct"`,
);

const GROQ_PRODUCT_QUERY = `*[_type == "product" && shopify.shopifyHandle == $handle][0]{video{url}}`;

const sizeGuideQuery = filterProjection(
  SettingSizeGuideSchema,
  `_type == "settingSizeGuide"`,
);

const biomechanicalFootbedQuery = filterProjection(
  SettingBiomechanicalFootbedSchema,
  `_type == "settingBiomechanicalFootbed"`,
);

const customisableFitQuery = filterProjection(
  SettingCustomisableFitSchema,
  `_type == "settingCustomisableFit"`,
);

const shippingAndReturnsQuery = filterProjection(
  SettingShippingAndReturnsSchema,
  `_type == "settingShippingAndReturns"`,
);

const shoeCareInstructionsQuery = filterProjection(
  SettingShoeCareInstructionsSchema,
  `_type == "settingShoeCareInstructions"`,
);

const PRODUCT_COLOUR_VARIANTS_QUERY = /* GraphQL */ `
  #graphql
  query getProducts(
    $filters: [ProductFilter!]
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    collectionByHandle(handle: "internal-use-all-shoes") {
      handle
      id
      products(first: 200, filters: $filters) {
        nodes {
          handle
          tags
          availableForSale
          featuredImage {
            url
            altText
            height
            width
          }
          styleNameMetafield: metafield(
            namespace: "custom"
            key: "netsuite_style_name"
          ) {
            key
            namespace
            value
          }
          statusMetafield: metafield(
            namespace: "custom"
            key: "netsuite_status"
          ) {
            value
          }
          howToStyleImages: metafield(
            namespace: "custom"
            key: "how_to_style_images"
          ) {
            references(first: 2) {
              nodes {
                ... on MediaImage {
                  alt
                  image {
                    url
                    height
                    width
                  }
                }
              }
            }
          }
          excludeSites: metafield(namespace: "custom", key: "exclude_sites") {
            references(first: 10) {
              nodes {
                __typename
                ... on Metaobject {
                  handle
                }
              }
            }
          }
          variants(first: 1) {
            nodes {
              selectedOptions {
                name
                value
              }
            }
          }
        }
      }
    }
  }
`;

// Types for product media returned from server.
type ProductMediaImage = { type: "image" } & ImageProps;
type ProductMediaVideo = {
  type: "video";
  coverImage: ImageProps;
  videoUrl: string;
};
type ProductMedia = ProductMediaImage | ProductMediaVideo;
type AsSeenOnMedia = ImageProps & { handle: string | null };

export type ProductVariant = Omit<
  ProductVariantFragment,
  "storeAvailability"
> & {
  /** Resolved colour of the variant */
  colour: string | null;
  /** Resolved size of the variant */
  size: string | null;
  /** Label for the variant selector */
  label: string;
  /** Status for the variant */
  status:
    | "PRE_ORDER"
    | "NOTIFY"
    | "UNAVAILABLE"
    | "AVAILABLE"
    | "LOW_STOCK"
    | "FINAL_SALE";
  /** Store availability without the "nodes" part */
  storeAvailability: ProductVariantFragment["storeAvailability"]["nodes"];
};

export async function getServerData(props: GetServerDataProps) {
  // Resolve context.
  const context = await contextFromServerDataProps(props);

  // Expand context.
  const {
    respond,
    notFound,
    groq,
    sanity,
    locale,
    urlContext,
    storefront,
    site,
    sectionResolver,
    productCardResolver,
    klaviyoItemResolver,
    googleAnalyticsItemResolver,
    sizeGuide: sizeGuideBasic,
  } = context;

  // Resolve handle.
  const handle = props.params?.["handle"];
  if ("string" !== typeof handle) throw new Error("No handle resolved.");

  // Fetch product from sanity and shopify.
  const [
    page,
    template,
    shopifyResult,
    sizeGuide,
    biomechanicalFootbedSheetContents,
    customisableFitSheetContents,
    shippingAndReturnsSheetContents,
    shoeCareInstructions,
  ] = await Promise.all([
    sanity.fetch(GROQ_PRODUCT_QUERY, { handle }),
    groq(productTemplateQuery),
    storefront.query(PRODUCT_BY_HANDLE_QUERY, { variables: { handle } }),
    groq(sizeGuideQuery),
    groq(biomechanicalFootbedQuery),
    groq(customisableFitQuery),
    groq(shippingAndReturnsQuery),
    groq(shoeCareInstructionsQuery),
  ]);

  // Get shopify product from result.
  const shopifyProduct = shopifyResult.product;

  // Get style name from product.
  const styleName = shopifyProduct?.styleNameMetafield?.value;

  // Product not found, return 404.
  if (!shopifyProduct || !template) return notFound();

  // Resolve sections from the template.
  // @ts-expect-error
  template.additionalFlexibleSections = await sectionResolver.resolve({
    sanitySections: template.additionalFlexibleSections,
  });

  // Resolve filtered sections (by product tag) from the template.
  // @ts-expect-error
  template.additionalSections = await (async () => {
    if (
      !template.additionalSections ||
      !Array.isArray(template.additionalSections)
    ) {
      return [];
    }

    const sectionsByTag = template.additionalSections.filter((section) => {
      if (
        !section.tags ||
        !Array.isArray(section.tags) ||
        0 === section.tags.length
      ) {
        return true;
      }

      return section.tags.some((tag) => shopifyProduct.tags.includes(tag));
    });

    return await sectionResolver.resolve({
      sanitySections: sectionsByTag,
    });
  })();

  // Has this product been hidden using the "Exclude sites" metafield?
  const productIsExcluded =
    !!shopifyProduct.excludeSites?.references?.nodes.find(
      (node) => "Metaobject" === node.__typename && site.handle === node.handle,
    );

  // If product excluded, return 404.
  if (productIsExcluded) return notFound();

  // Resolve sibling products.
  const colourVariants = await (async () => {
    // No style name.
    if (!styleName) return [];

    // Get variants from storefront.
    const res = await storefront.query(PRODUCT_COLOUR_VARIANTS_QUERY, {
      variables: {
        filters: [
          // Products with the same style name.
          {
            productMetafield: {
              key: "netsuite_style_name",
              namespace: "custom",
              value: styleName,
            },
          },
        ],
      },
    });

    // No collection found.
    if (!res.collectionByHandle) return [];

    // Return variants which are either available, or of "Seasonal" or "Core" status.
    return res.collectionByHandle.products.nodes
      .filter((product) => {
        // Has this product been hidden using the "Exclude sites" metafield?
        if (
          !!product.excludeSites?.references?.nodes.find(
            (node) =>
              "Metaobject" === node.__typename && site.handle === node.handle,
          )
        ) {
          return false;
        }

        // Get metafield values.
        const productStatus = product.statusMetafield?.value ?? null;
        const productStyleName = product.styleNameMetafield?.value ?? null;

        /**
         * These filters are technically in the storefront query "filters" attribute, but because these
         * filters are configured with the Shopify Search & Filter app, there is a chance that they
         * could get broken. Here, we are doing the same checks again just in case the query
         * ignores one of the filters.
         */
        const inQueryFilters = styleName === productStyleName;

        /**
         * These filters cannot be included in the storefront query because "filters" does not support OR.
         */
        const postQueryFilters =
          product.availableForSale === true ||
          productStatus === "Seasonal" ||
          productStatus === "Core";

        // Return only products which match both sets of filters.
        return !!inQueryFilters && !!postQueryFilters;
      })
      .map((product) => {
        const howToStyleImages: ImageProps[] = product.howToStyleImages
          ?.references
          ? product.howToStyleImages.references.nodes
              .filter(({ image }) => !!image)
              .map(({ alt, image }) => {
                // Should never reach here, as filtered above.
                if (!image) throw new Error("No image on metafield.");

                return {
                  data: image,
                  alt: alt ?? "",
                  source: "shopify",
                };
              })
          : [];

        return {
          siblingHandle: product.handle,
          siblingColour: product.variants.nodes[0].selectedOptions.find(
            (option) =>
              option?.name?.includes("Color") ||
              option?.name?.includes("Colour"),
          )?.value,
          image: {
            source: "shopify",
            data: {
              url: product.featuredImage?.url,
              height: product.featuredImage?.height,
              width: product.featuredImage?.width,
            },
          },
          siblingHowToStyleImages: howToStyleImages,
        };
      });
  })();

  // Resolve how to style siblings handle from the metafield
  const howToStyleSiblingsHandle = shopifyProduct.howToStyleSiblingsHandle
    ?.references
    ? shopifyProduct.howToStyleSiblingsHandle.references.nodes.map(
        (node) => node.handle,
      )
    : [];

  // Resolve how to style siblings from the colour variants and how to style siblings handle
  const howToStyleSiblings = howToStyleSiblingsHandle
    .map((handle: string) => {
      const variant = colourVariants.find(
        (variant) => variant.siblingHandle === handle,
      );
      if (!variant) return null;

      return {
        howToStyleImages: variant.siblingHowToStyleImages,
        handle: variant.siblingHandle,
        colour: variant.siblingColour,
      };
    })
    .filter(Boolean);

  // Fetch shoe care material from shopify metafield.
  const shoeCareMaterial = shopifyProduct.shoeCareMaterial?.reference
    ? shopifyProduct.shoeCareMaterial.reference.handle
    : null;

  // Convert shoeCareInstructions to array of object indexed by materialHandle
  const shoeCareInstructionsByMaterialHandle =
    !!shoeCareInstructions &&
    shoeCareInstructions.shoeCareInstructions.reduce(
      (acc, item) => {
        acc[item.materialHandle] = item;
        return acc;
      },
      {} as Record<
        string,
        (typeof shoeCareInstructions.shoeCareInstructions)[0]
      >,
    );

  // Get shoe care instruction for the material
  const shoeCareInstruction =
    shoeCareMaterial && shoeCareInstructionsByMaterialHandle
      ? shoeCareInstructionsByMaterialHandle[shoeCareMaterial]
      : null;

  const extractComplementaryProductsAndRecommendations = (
    products,
  ): ProductCard[] => {
    return products
      .filter((product) => {
        if (!product.availableForSale) return false;

        // Has this product been hidden using the "Exclude sites" metafield?
        if (
          !!product.excludeSites?.references?.nodes.find(
            (node) =>
              "Metaobject" === node.__typename && site.handle === node.handle,
          )
        ) {
          return false;
        }

        return true;
      })
      .map((shopifyProduct) => productCardResolver.resolve({ shopifyProduct }));
  };

  // Fetch product recommendations from shopify.
  const productRecommendations = await (async () => {
    const shopifyData = await storefront.query(PRODUCT_RECOMMENDATIONS_QUERY, {
      variables: { productId: shopifyProduct.id, intent: "RELATED" },
    });

    if (!shopifyData.productRecommendations) return [];

    return extractComplementaryProductsAndRecommendations(
      shopifyData.productRecommendations,
    );
  })();

  // Fetch shoe care upsells from shopify metafield.
  const shoeCareUpsellProducts = await (async () => {
    const productIds = shopifyProduct.shoeCareUpsell
      ? (JSON.parse(shopifyProduct.shoeCareUpsell.value) as string[])
      : [];

    return (
      await Promise.all(
        productIds.map((productId) =>
          storefront
            .query(PRODUCT_CARD_BY_ID_QUERY, { variables: { productId } })
            .then((res) => res.product),
        ),
      )
    )
      .filter(Boolean)
      .filter((shopifyProduct) => !!shopifyProduct.availableForSale)
      .map((shopifyProduct) => productCardResolver.resolve({ shopifyProduct }));
  })();

  // Fetch complementary products from shopify.
  const upsellProducts = await (async () => {
    const shopifyData = await storefront.query(PRODUCT_RECOMMENDATIONS_QUERY, {
      variables: { productId: shopifyProduct.id, intent: "COMPLEMENTARY" },
    });

    if (!shopifyData.productRecommendations) return [];

    const complementaryProducts =
      extractComplementaryProductsAndRecommendations(
        shopifyData.productRecommendations,
      );

    // If there are complementary products, return them, otherwise return the shoe care upsell products.
    return complementaryProducts.length > 0
      ? complementaryProducts
      : shoeCareUpsellProducts;
  })();

  // Resolve flexible sections in the size guide template.
  const sizeGuideSheetContents = sizeGuide
    ? {
        ...sizeGuide,
        sizingAdvice: shopifyProduct.sizingAdvice
          ? shopifyProduct.sizingAdvice.value
          : null,
        flexibleSections: sizeGuide.flexibleSections
          ? await sectionResolver.resolve({
              sanitySections: sizeGuide.flexibleSections,
            })
          : null,
      }
    : null;

  // Determine the template type.
  const templateType = (() => {
    if (shopifyProduct.isGiftCard) {
      return "giftcard";
    }

    return "product";
  })();

  // Compute aggregate rating for product.
  const aggregateRating: WithContext<AggregateRating> | null =
    !!shopifyProduct.reviewsCount && !!shopifyProduct.reviewsRating
      ? {
          "@context": "https://schema.org",
          "@type": "AggregateRating",
          ratingValue: shopifyProduct.reviewsRating.value,
          ratingCount: parseInt(shopifyProduct.reviewsCount.value),
        }
      : null;

  /**
   * Construct a JSON-LD schema for product.
   * @see https://developers.google.com/search/docs/appearance/structured-data/product
   */
  const productSchema: SeoSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: shopifyProduct.title,
    url: urlContext.canonicalUrl,
    gtin13: parseGid(shopifyProduct.id).id,
    description: shopifyProduct.seo.description ?? undefined,
    image: shopifyProduct.featuredImage
      ? {
          "@type": "ImageObject",
          url: shopifyProduct.featuredImage.openGraphUrl,
          description: shopifyProduct.seo.description ?? undefined,
        }
      : undefined,
    brand: { "@type": "Brand", name: "Bared Footwear" },
    sku: shopifyProduct.variants.edges[0]?.node.sku ?? undefined,
    offers: {
      "@type": "AggregateOffer",
      lowPrice: shopifyProduct.priceRange.minVariantPrice.amount,
      highPrice: shopifyProduct.priceRange.maxVariantPrice.amount,
      priceCurrency: shopifyProduct.priceRange.maxVariantPrice.currencyCode,
      offerCount: shopifyProduct.variants.edges.length,
      offers: shopifyProduct.variants.edges.map(({ node }) => ({
        "@type": "Offer",
        price: node.price.amount,
        priceCurrency: node.price.currencyCode,
        sku: node.sku ?? undefined,
        availability: "https://schema.org/InStock",
        url: `${urlContext.canonicalUrl}?variant=${parseGid(node.id).id}`,
      })),
    },
    ...(aggregateRating ? { aggregateRating } : {}),
  };

  // Resolve seo config.
  const seo = seoConfigForPage({
    context,
    schemas: [productSchema],
    description: shopifyProduct.seo.description,
    title: shopifyProduct.seo.title ?? shopifyProduct.title,
    media: shopifyProduct.featuredImage?.openGraphUrl,
  });

  // Resolve description calloout content from the metafield
  const descriptionCalloutContent = shopifyProduct.descriptionCallout
    ? JSON.parse(shopifyProduct.descriptionCallout.value)
    : null;

  const seoFeatureDescriptionContent = shopifyProduct.seoFeatureDescription
    ? JSON.parse(shopifyProduct.seoFeatureDescription.value)
    : null;

  // Resolve how to style content from the metafield
  const howToStyleContent = shopifyProduct.howToStyleContent
    ? JSON.parse(shopifyProduct.howToStyleContent.value)
    : null;

  // Resolve sustainable feature from the product metafield
  const sustainableFeatures = shopifyProduct.sustainableFeatures?.value && {
    id: shopifyProduct.sustainableFeatures?.key ?? "sustainable_features",
    title: "Sustainable Features",
    value: shopifyProduct.sustainableFeatures.value,
  };

  // Resolve shoe care guide from the product metafield
  const shoeCareGuide = shopifyProduct.shoeCareGuide?.value && {
    id: shopifyProduct.shoeCareGuide?.key ?? "shoe_care_guide",
    title: "Shoe Care",
    value: shopifyProduct.shoeCareGuide.value,
  };

  // Resolve how to style images from the metafield
  const howToStyleImages: ImageProps[] = shopifyProduct.howToStyleImages
    ?.references
    ? shopifyProduct.howToStyleImages.references.nodes
        .filter(({ image }) => !!image)
        .map(({ alt, image }) => {
          // Should never reach here, as filtered above.
          if (!image) throw new Error("No image on metafield.");

          return {
            data: image,
            alt: alt ?? "",
            source: "shopify",
          };
        })
    : [];

  // Resolve as seen on images from the metafield
  const asSeenOnImages: AsSeenOnMedia[] = shopifyProduct.asSeenOnImages
    ?.references
    ? shopifyProduct.asSeenOnImages.references.nodes
        .filter(({ image }) => !!image?.reference?.image)
        .map(({ image, socialHandle }) => {
          if (!image?.reference?.image)
            throw new Error("No image on metafield.");

          return {
            source: "shopify",
            data: image.reference.image,
            handle: !socialHandle ? null : "@" + socialHandle.value,
          };
        })
    : [];

  // Extract video URL from Sanity.
  const videoUrl = page?.video?.url ?? null;

  // Start product media with the images from shopify.
  let productMedia = shopifyProduct.images.nodes.map((data): ProductMedia => {
    /**
     * Shopify product images which have the substring "_cover" in their name are
     * the cover image for a video, stored in Sanity.
     * @todo Store actual videos in Shopify, not just cover images.
     */
    if (videoUrl && data.url.toLowerCase().includes("_cover")) {
      return {
        videoUrl,
        type: "video",
        coverImage: {
          source: "shopify",
          data,
          alt: data.altText || shopifyProduct.title,
        },
      };
    }

    return {
      data,
      type: "image",
      source: "shopify",
      alt: data.altText || shopifyProduct.title,
    };
  });

  // Get the products name from the relevant products metafield value
  const productName = !!shopifyProduct.styleNameMetafield?.value
    ? shopifyProduct.styleNameMetafield.value
    : null;

  // Get the product color.
  const productColor =
    shopifyProduct.options.find((option) =>
      ["color", "colour"].includes(option.name.toLowerCase()),
    )?.optionValues[0]?.name ?? null;

  // Get the products gender from the products metafield
  const productGender = (() => {
    if (!shopifyProduct.genderMetafield?.value) return null;
    const parse = productGenderSchema.safeParse(
      shopifyProduct.genderMetafield.value,
    );
    return parse.success ? parse.data : null;
  })();

  // Get the product status from the product metafield
  const productStatus = !!shopifyProduct.statusMetafield?.value
    ? shopifyProduct.statusMetafield.value
    : null;

  // Review group is a tag like "RGroup_Sanderlings" used to fetch reviews
  // from reviews.io relating to the product
  const reviewGroup =
    shopifyProduct.tags.find((tag) =>
      tag.toLowerCase().startsWith("rgroup_"),
    ) ?? null;

  // Reviews.io requires a SKU when adding a question or review. Here, we are
  // just selecting the first variant SKU we can find.
  const reviewSku =
    shopifyProduct.variants.edges.find((edge) => !!edge.node.sku)?.node.sku ??
    null;

  // Enable the "Notify when back in stock" functionality.
  const enableNotify = productStatus
    ? ["Core", "Seasonal"].includes(productStatus)
    : false;

  // Is at least one variant available for sale?
  const availableForSale = shopifyProduct.availableForSale;

  // Get primary collection from the products metafield
  const primaryCollection = !!shopifyProduct.primaryCollection
    ? shopifyProduct.primaryCollection.reference
    : null;

  // Resolve an array of breadcrumbs.
  const breadcrumbs = (() => {
    const breadcrumbs: { title: string; url: string }[] = [];

    // Gift cards do not have breadcrumbs.
    if (shopifyProduct.isGiftCard) return breadcrumbs;

    // If we have a gender, append the gender collection page.
    if (productGender) {
      if ("Mens" === productGender) {
        breadcrumbs.push({
          title: "Men's",
          url: "/collections/all-mens",
        });
      }

      if ("Womens" === productGender) {
        breadcrumbs.push({
          title: "Women's",
          url: "/collections/all-womens",
        });
      }
    }

    // If we have a primary collection, append that.
    if (primaryCollection) {
      breadcrumbs.push({
        title: primaryCollection.title,
        url: `/collections/${primaryCollection.handle}`,
      });
    }

    return breadcrumbs;
  })();

  // If the product is not available for sale and notify is off, generate an
  // out of stock message with a link to the primary collection.
  const outOfStockMessageHtml = (() => {
    if (availableForSale || enableNotify) return null;

    if (primaryCollection) {
      return `<p>Oh no, you've missed out! This is out of stock. Check out our <a href="/collections/${primaryCollection.handle}">${primaryCollection.title}</a> for more.</p>`;
    }

    return "<p>Oh no, you've missed out! This is out of stock.</p>";
  })();

  // Determine if the footbed image should be hidden.
  const hideFootbedImage = !!shopifyProduct.hideFootbedImage?.value
    ? JSON.parse(shopifyProduct.hideFootbedImage.value)
    : false;

  // Resolve a footbed image asset for the gender.
  const footbedImageAsset =
    "Womens" === productGender
      ? template.womensFootbedImage?.asset
      : "Mens" === productGender
        ? template.mensFootbedImage?.asset
        : null;

  // If we have a footbed image, splice that into 3rd position.
  if (footbedImageAsset && !hideFootbedImage) {
    productMedia.splice(2, 0, {
      type: "image",
      source: "sanity",
      data: footbedImageAsset,
      alt: "Our Biomechanical Footbed: Engineered For Support. Features include: Metatarsal Dome - aims to prevent discomfort by reducing pressure in the ball of the foot; Cuboid Notch - helps to stabilise your foot to promote better foot function; Midfoot Arch Support - provides cushioned support for optimal comfort; Heel Cup - cushions the heel, providing shock absorption to aid in the prevention of heel pain.",
    });
  }

  // Resolve navigation root path.
  const navigationRootPath =
    productGender === "Mens"
      ? "/men"
      : productGender === "Womens"
        ? "/women"
        : "/";

  // Resolve a plain array of variant SKUs.
  const variantSkus = shopifyProduct.variants.edges.reduce((acc, cur) => {
    return [...acc, ...(cur.node.sku ? [cur.node.sku] : [])];
  }, [] as string[]);

  /**
   * Determine the threshold for "low stock" messaging.
   * If a variant has less or equal to this amount, it will be labelled as low stock.
   */
  const lowStockThreshold = 3;

  // Determine if the product is final sale.
  const isFinalSale = !!shopifyProduct.finalSale?.value
    ? JSON.parse(shopifyProduct.finalSale.value)
    : false;

  // Determine if the product has a fit kit included.
  const isFitKitIncluded = !!shopifyProduct.fitKitIncluded?.value
    ? JSON.parse(shopifyProduct.fitKitIncluded.value)
    : false;

  // Resolve the size guide for this gender.
  const sizeGuideForGender =
    "Womens" === productGender
      ? sizeGuideBasic.women
      : "Mens" === productGender
        ? sizeGuideBasic.men
        : null;

  // Resolve an array of variants for the product.
  const productVariants: ProductVariant[] = shopifyProduct.variants.edges.map(
    ({ node: productVariant }) => {
      // Resolve colour of the variant.
      const colour =
        productVariant.selectedOptions.find((option) =>
          ["color", "colour"].includes(option.name.toLowerCase()),
        )?.value ?? null;

      // Resolve size of the variant.
      const size =
        productVariant.selectedOptions.find(
          (option) => "size" === option.name.toLowerCase(),
        )?.value ?? null;

      /**
       * Determine if this variant is on preorder:
       * - The variant must be available for sale
       * - The variant must have a quantity available
       * - The variant must not be available in any Physical + Oakleigh store. Preorder location does not feature in store availability.
       */
      // TODO: Need to find long term solution, Following logic falls flat when we disable C&C and store availability API is empty.
      const isPreOrder =
        productVariant.availableForSale &&
        (productVariant.quantityAvailable ?? 0) > 0 &&
        productVariant.storeAvailability.nodes.every(
          (store) => !store.available || store.quantityAvailable <= 0,
        );

      // Determine the quantity available for this variant.
      const quantityAvailable = !!productVariant.quantityAvailable
        ? productVariant.quantityAvailable
        : 0;

      // Resolve a status for the "Add to cart" button.
      const status: ProductVariant["status"] = productVariant.availableForSale
        ? isPreOrder
          ? "PRE_ORDER"
          : isFinalSale
            ? "FINAL_SALE"
            : quantityAvailable <= lowStockThreshold
              ? "LOW_STOCK"
              : "AVAILABLE"
        : enableNotify
          ? "NOTIFY"
          : "UNAVAILABLE";

      // Resolve a label with a size conversion if possible.
      const label = (() => {
        // If we don't have a size, output the variant title.
        if (!size) return productVariant.title;

        // We don't have a size guide for this geneder, return just the size.
        if (!sizeGuideForGender) return size;

        // Try to resolve a US size from the size guide.
        const usSize = sizeGuideForGender.find(
          (row) => row.bared === size,
        )?.us_au;

        // Could not resolve a US size, return just the size.
        if (!usSize) return size;

        // Return size with US conversion appended.
        return `${size} (US ${usSize})`;
      })();

      return {
        ...productVariant,
        size,
        label,
        colour,
        status,
        storeAvailability: productVariant.storeAvailability.nodes,
      };
    },
  );

  const { price, compareAtPrice } = context.productPriceResolver.resolve({
    shopifyProduct,
  });

  // Resolve a badge.
  const badge = context.productBadgeResolver.resolve({
    shopifyProduct,
    prices: { price, compareAtPrice },
  });

  const googleAnalyticsItem =
    googleAnalyticsItemResolver.resolveFromShopifyProduct({
      shopifyProduct,
      price,
      compareAtPrice,
    });

  const klaviyoItem = klaviyoItemResolver.resolveFromShopifyProduct({
    shopifyProduct,
    price,
    compareAtPrice,
  });

  const klaviyoViewedProductItem = klaviyoItemResolver.resolveViewedProductItem(
    {
      shopifyProduct,
      price,
      compareAtPrice,
    },
  );

  // Enable TrueFit
  const enableTrueFit = shopifyProduct.tags?.includes("trial:truefit") ?? false;

  // Respond with server data.
  return respond({
    handle,
    seo,
    badge,
    reviewSku,
    reviewGroup,
    descriptionCalloutContent,
    seoFeatureDescriptionContent,
    template,
    isFinalSale,
    isFitKitIncluded,
    variantSkus,
    templateType,
    productMedia,
    shopifyProduct,
    productVariants,
    navigationRootPath,
    colourVariants,
    productName,
    productColor,
    productGender,
    productStatus,
    enableNotify,
    productRecommendations,
    upsellProducts,
    primaryCollection,
    shoeCareGuide,
    shoeCareUpsellProducts,
    howToStyleContent,
    howToStyleImages,
    howToStyleSiblings,
    asSeenOnImages,
    sustainableFeatures,
    sizeGuideSheetContents,
    biomechanicalFootbedSheetContents,
    customisableFitSheetContents,
    shippingAndReturnsSheetContents,
    shoeCareInstruction,
    price,
    compareAtPrice,
    klaviyoItem,
    klaviyoViewedProductItem,
    googleAnalyticsItem,
    enableTrueFit,
    availableForSale,
    breadcrumbs,
    outOfStockMessageHtml,
  } as const);
}

export type ServerData = Awaited<ReturnType<typeof getServerData>>["props"];

export type ServerDataSuccess = Extract<ServerData, { status: 200 }>;

export default function ProductPage(props: PageProps<ServerData>) {
  const { serverData } = props;

  /**
   * The key prop forces the component to remount when the URI changes rather than just passing
   * through the updated props. We have deep components which do not re-render properly when
   * their props change, so currently remounting is our only option to remain consistent.
   * @see https://bared.atlassian.net/browse/ECOM-2579
   * @see https://bared-melbourne.slack.com/archives/C045GT6BNAU/p1715903163431309
   */
  if (200 === serverData.status) {
    return (
      <>
        <ProductProvider serverData={serverData} key={props.uri}>
          <ReviewsProvider>
            <ProductPageInner {...props} />
          </ReviewsProvider>
        </ProductProvider>
        <Script
          strategy="idle"
          src="https://widget.reviews.io/rating-snippet/dist.js"
        />
      </>
    );
  }

  // Page not found, render error page.
  if (404 === serverData.status) {
    return <ErrorPage page={serverData.errorPage} />;
  }

  return null;
}

function ProductPageInner({ serverData }: PageProps<ServerDataSuccess>) {
  const site = useSite();
  const trendii = useTrendii();
  const klaviyo = useKlaviyo();
  const tripleWhale = useTripleWhale();
  const googleTagManager = useGoogleTagManager();
  const nostoAnalytics = useNostoAnalytics();

  // Send tracking events when server data changes.
  useEffect(() => {
    const { shopifyProduct, klaviyoViewedProductItem } = serverData;
    const productId = shopifyProduct.id.replace("gid://shopify/Product/", "");

    const trendiiProductData = {
      id: shopifyProduct.id,
      title: shopifyProduct.title,
      sku: shopifyProduct.variants.edges[0]?.node.sku,
      price: shopifyProduct.priceRange.maxVariantPrice.amount,
      currency: shopifyProduct.priceRange.maxVariantPrice.currencyCode,
    };
    trendii.trackPageLoad(trendiiProductData);

    tripleWhale.trackPageLoad("Product");

    googleTagManager.push({
      event: "view_item",
      properties: {
        items: [serverData.googleAnalyticsItem],
        value: serverData.googleAnalyticsItem.price,
      },
    });

    klaviyo.track("Viewed Product", klaviyoViewedProductItem);
    nostoAnalytics.trackProductPageView(productId);
  }, [
    googleTagManager,
    nostoAnalytics,
    tripleWhale,
    trendii,
    klaviyo,
    serverData,
  ]);

  const updateRecentlyViewed = useUpdateRecentlyViewed();

  // Update recently viewed cookie.
  useEffect(() => {
    updateRecentlyViewed({ productHandle: serverData.handle });
  }, [serverData, updateRecentlyViewed]);

  if ("giftcard" === serverData.templateType) {
    return <GiftCard />;
  }

  // Render reviews sentiment widget.
  const reviewsSummary = (
    <ReviewsSentimentWidget
      productHandle={serverData.handle}
      variantSkus={serverData.variantSkus}
    />
  );

  return (
    <Product
      reviewsSummary={reviewsSummary}
      reviewNugget={<ReviewNugget variantSkus={serverData.variantSkus} />}
      imageGallery={
        <ProductImageGallery
          howToStyle={
            !!serverData.howToStyleContent &&
            serverData.howToStyleImages.length > 0
          }
          modalId="product-image-gallery"
          badge={serverData.badge}
          productMedia={serverData.productMedia}
          key={`ProductImageGallery-${serverData.handle}`} // Ensure that scrollable nodes remount when navigating between products.
        />
      }
      featureAndShoeCareSheet={
        <FeatureAndShoeCareSheet
          gender={serverData.productGender}
          productName={serverData.productName}
        />
      }
      howToStyleSection={
        0 < serverData.howToStyleImages.length &&
        !!serverData.howToStyleContent ? (
          <HowToStyleSection
            howToStyleImages={serverData.howToStyleImages}
            howToStyleContent={serverData.howToStyleContent}
            howToStyleSiblings={serverData.howToStyleSiblings}
            key={`HowToStyleSection-${serverData.handle}`} // Ensure that scrollable nodes remount when navigating between products.
          />
        ) : null
      }
      productRecommendations={
        !!serverData.productRecommendations &&
        0 < serverData.productRecommendations.length ? (
          <ProductRecommendations
            productRecommendations={serverData.productRecommendations}
            currentProductHandle={serverData.handle}
          />
        ) : null
      }
      productUpsells={
        !!serverData.upsellProducts && 0 < serverData.upsellProducts.length ? (
          <ProductUpsells upsellProducts={serverData.upsellProducts} />
        ) : null
      }
      asSeenOnSection={
        serverData.asSeenOnImages.length >= 4 ? (
          <AsSeenOnSection
            asSeenOnImages={serverData.asSeenOnImages}
            key={`AsSeenOnSection-${serverData.handle}`}
          />
        ) : null
      }
      swatches={
        serverData.colourVariants.length >= 1 ? (
          <Swatches
            colourVariants={serverData.colourVariants}
            handle={serverData.handle}
          />
        ) : null
      }
      seoFeatureDescription={
        !!serverData.seoFeatureDescriptionContent && (
          <SeoFeatureDescription
            seoFeatureDescription={serverData.seoFeatureDescriptionContent}
          />
        )
      }
      // Render sibling colours which then will be used as style name for reviews
      siblingColours={serverData.colourVariants
        .map(({ siblingColour }) => siblingColour)
        .filter(Boolean)}
    >
      <AddToCartStack
        countryCode={site.countryCode}
        callout={serverData.descriptionCalloutContent}
        enableFloatingBar
        enableTrueFit={serverData.enableTrueFit}
        enableFindInStore={true}
        gender={serverData.productGender}
        isFitKitIncluded={serverData.isFitKitIncluded}
        locale={site.locale}
        siteHandle={site.handle}
        sizingAdvice={serverData.sizeGuideSheetContents?.sizingAdvice}
      />
    </Product>
  );
}

export function Head({ serverData }: HeadProps<ServerData>) {
  if (!serverData) return null;

  return (
    <>
      <link
        rel="stylesheet"
        href="https://widget.reviews.io/rating-snippet/dist.css"
      />
      <MetaComponent
        meta={getSeoMeta(serverData.context.rootData.seo, serverData.seo)}
      />
    </>
  );
}

const productGenderSchema = z.union([z.literal("Mens"), z.literal("Womens")]);
